import type { BootstrapData } from '../../types'
import type { ILocationManager, SessionServiceAPI } from '@wix/thunderbolt-symbols'
import { ViewerPlatformEssentials } from '@wix/fe-essentials-viewer-platform'
import { BOOTSTRAP_DATA, SESSION_SERVICE, PLATFORM_ESSENTIALS, LOCATION_MANAGER } from './moduleNames'
import type { HttpClientOptions } from '@wix/fe-essentials-viewer-platform/http-client'
import type { GlobalConfig } from '@wix/fe-essentials-viewer-platform/panorama-client'
import { createGlobalConfig } from '@wix/fe-essentials-viewer-platform/panorama-client'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

const getSentry = () => (process.env.browser ? require('@wix/fe-essentials-viewer-platform/sentry') : null)
const currencyCodeRegex = /^[A-Z]{3}$/
let panoramaGlobalConfig: GlobalConfig

function PlatformEssentials(
	bootstrapData: BootstrapData,
	sessionService: SessionServiceAPI,
	locationManager: ILocationManager
) {
	const {
		platformEnvData: {
			site: { experiments, viewMode },
			location: { externalBaseUrl },
			window: { isSSR },
			bi: { pageData, viewerSessionId },
			multilingual,
			commonConfig,
		},
	} = bootstrapData
	const isSiteMode = viewMode === 'Site'

	if (!panoramaGlobalConfig) {
		const globalConfig = createGlobalConfig()

		if (viewerSessionId) {
			globalConfig.setSessionId(viewerSessionId)
		}

		panoramaGlobalConfig = globalConfig
	}

	const setBaseUrlToExternalBaseUrl = isExperimentOpen(
		experiments,
		'specs.thunderbolt.essentials_base_url_external_base_url'
	)
	const baseUrl = setBaseUrlToExternalBaseUrl ? externalBaseUrl : isSSR ? new URL(externalBaseUrl).origin : ''

	const getMultilingualOptions = (): HttpClientOptions['multilingualOptions'] => {
		if (!multilingual) {
			return
		}

		const {
			currentLanguage: { languageCode: lang, locale, isPrimaryLanguage },
		} = multilingual

		return {
			lang,
			locale,
			isPrimaryLanguage,
		}
	}

	const getCurrency = () => {
		const currency = locationManager.getLocation().searchParams.get('currency')

		return currency && currencyCodeRegex.test(currency) ? currency : undefined
	}

	const isWorker = !isSSR && typeof window === 'undefined'
	// Forward worker panorama events via postMessage so they survive MPA navigation in
	// published sites. In Editor/Preview the worker is an iframe wrapper and the main
	// thread's listener is bound to the iframe element — postMessages from the iframe
	// reach the parent window instead and are lost. Keep the worker batching default.
	if (isWorker && isSiteMode) {
		panoramaGlobalConfig.initBatchQueue({
			enqueue(eventPayload: Record<string, unknown>) {
				;(self as unknown as Worker).postMessage({
					type: 'workerBatchEvent',
					data: eventPayload,
				})
			},
		})
	}

	const platformEssentials = new ViewerPlatformEssentials({
		conductedExperiments: experiments,
		isSSR,
		baseUrl,
		multilingualOptions: getMultilingualOptions(),
		getCurrency,
		language: commonConfig.language,
		metaSiteId: bootstrapData.platformEnvData.location.metaSiteId,
		appsConductedExperiments: bootstrapData.essentials.appsConductedExperiments,
		panoramaGlobalConfig,
		Sentry: getSentry(),
		pageData,
		fetch: self.fetch,
		getAppToken(appDefId) {
			return sessionService.getInstance(appDefId)
		},
	})

	return platformEssentials
}

export default {
	factory: PlatformEssentials,
	deps: [BOOTSTRAP_DATA, SESSION_SERVICE, LOCATION_MANAGER],
	name: PLATFORM_ESSENTIALS,
}
